// =============================================
// WATERDROP CLICKER — the idle layer
// =============================================
// Tap the well for drops, spend drops on condensers that produce drops on
// their own, and the production keeps running while the game is closed.
//
// The load-bearing decision (see docs/ROADMAP-modes.md): drops are the
// clicker's INTERNAL currency and buy nothing outside it. Real payouts come
// from lifetime-production milestones instead. If drops converted directly
// into gold or gems, the clicker would immediately out-earn actually playing
// the game, and every other mode would become pointless — which is the usual
// way an idle layer bolted onto an action game eats the action game.

export const CLICKER_BUILDINGS = [
  { id: 'condenser', name: 'Tin-Foil Condenser', icon: 'gem',        base: 15,        growth: 1.15, rate: 1,        cycle: 1.0,   desc: 'Wrapped in pure aluminium to block 5G water evaporation.' },
  { id: 'net',       name: 'Chemtrail Siphon',   icon: 'shield',     base: 120,       growth: 1.16, rate: 5,        cycle: 2.5,   desc: 'Siphons pure moisture directly from suspicious contrails.' },
  { id: 'fluoride',  name: 'Fluoride Scrubber',  icon: 'pVamp',      base: 450,       growth: 1.16, rate: 12,       cycle: 5.0,   desc: 'Restores untamed kinetic fury to municipal tap water.' },
  { id: 'trap',      name: 'HAARP Cloud Trap',   icon: 'orb',        base: 1400,      growth: 1.17, rate: 28,       cycle: 10.0,  desc: 'Captures synthetic stormclouds generated by secret antennas.' },
  { id: 'aquifer',   name: 'Deep State Borehole',icon: 'beam',       base: 7000,      growth: 1.18, rate: 110,      cycle: 20.0,  desc: 'Drills past bunker level 4 into prehistoric water tables.' },
  { id: 'coil',      name: 'Subliminal Coil',    icon: 'bolt',       base: 48000,     growth: 1.19, rate: 490,      cycle: 40.0,  desc: 'Pulses watercooler frequencies that force rain to obey.' },
  { id: 'engine',    name: 'Pyramid Dynamo',     icon: 'meteor',     base: 360000,    growth: 1.20, rate: 2400,     cycle: 80.0,  desc: 'Harnesses sacred geometry to move the tides at will.' },
  { id: 'bureau',    name: 'Reptilian Bureau',   icon: 'ui_eye',     base: 2400000,   growth: 1.21, rate: 12500,    cycle: 160.0, desc: 'Extracts condensed tears from heavily redacted government files.' },
  { id: 'annunaki',  name: 'Annunaki Wellspring',icon: 'trident',    base: 18000000,  growth: 1.22, rate: 72000,    cycle: 320.0, desc: 'Direct pipeline to the orbital mothership freshwater reservoir.' },
  { id: 'monolith',  name: 'Cosmic Monolith Core',icon: 'ui_void',   base: 140000000, growth: 1.23, rate: 480000,   cycle: 640.0, desc: 'Pumps eldritch H2O from the space between dimensions.' },
];

export const CLICKER_MAPS = [
  {
    id: 'gobekli',
    name: 'Göbekli Tepe Sanctuary',
    era: '10,000 BC',
    theme: 'municipal',
    desc: 'The ancient stone circle where the first drop was worshipped.',
    accent: 0xe0a050,
  },
  {
    id: 'stonehenge',
    name: 'Stonehenge Solstice & Crop Circles',
    era: '3,000 BC',
    theme: 'haarp',
    desc: 'Trilithon sunbeam alignment amidst glowing mysterious wheat patterns.',
    accent: 0x4dffd4,
  },
  {
    id: 'pyramid',
    name: 'The Great Seal Pyramid',
    era: '1776 AD',
    theme: 'pyramid',
    desc: 'Stepped masonry with the floating radiant Eye of Providence.',
    accent: 0xffd166,
  },
  {
    id: 'dumbtower',
    name: 'DUMB TOWER & Golden Escalator',
    era: 'Modern Era',
    theme: 'deepstate',
    desc: 'Ultra-luxurious gold skyscraper drilling through bunker level 5.',
    accent: 0xffaa00,
  },
  {
    id: 'armold_mansion',
    name: "Armold's Belgian Blue Mansion",
    era: 'Classified',
    theme: 'fluoride',
    desc: 'Heavy iron weights, mini-horse stable, and heavy miniguns.',
    accent: 0x39ff14,
  },
  {
    id: 'bohemian',
    name: 'Bohemian Grove (Blood Moon)',
    era: 'The Deep Night',
    theme: 'cosmic',
    desc: '40-foot stone owl statue in dark redwoods under a blood moon.',
    accent: 0xff3366,
  },
];

export const CLICKER_RESEARCH_TREE = [
  { id: 'tech_condenser', buildingId: 'condenser', name: 'Aluminium Alchemy', cost: 5, mult: 3.0, desc: '×3 Tin-Foil Condenser production.' },
  { id: 'tech_siphon', buildingId: 'net', name: 'Cloud Seeding Protocol', cost: 15, mult: 3.0, desc: '×3 Chemtrail Siphon production.' },
  { id: 'tech_fluoride', buildingId: 'fluoride', name: 'Kinetic Tap Fury', cost: 40, mult: 4.0, desc: '×4 Fluoride Scrubber production.' },
  { id: 'tech_haarp', buildingId: 'trap', name: 'Ionospheric Resonance', cost: 100, mult: 5.0, desc: '×5 HAARP Cloud Trap production.' },
  { id: 'tech_dumb', buildingId: 'aquifer', name: 'Golden Escalator Drill', cost: 250, mult: 5.0, desc: '×5 Deep State Borehole production.' },
  { id: 'tech_coil', buildingId: 'coil', name: 'Subliminal Hypnosis 432Hz', cost: 600, mult: 6.0, desc: '×6 Subliminal Coil production.' },
  { id: 'tech_pyramid', buildingId: 'engine', name: 'Sacred Capstone Beam', cost: 1500, mult: 8.0, desc: '×8 Pyramid Dynamo production.' },
  { id: 'tech_reptilian', buildingId: 'bureau', name: 'Classified Reptilian Docket', cost: 4000, mult: 10.0, desc: '×10 Reptilian Bureau production.' },
  { id: 'tech_annunaki', buildingId: 'annunaki', name: 'Stargate Deep Pipeline', cost: 12000, mult: 15.0, desc: '×15 Annunaki Wellspring production.' },
  { id: 'tech_monolith', buildingId: 'monolith', name: 'Eldritch Void Straw', cost: 50000, mult: 25.0, desc: '×25 Cosmic Monolith Core production.' },
  { id: 'tech_armold_rush', name: 'Belgian Blue Hyper-Pump', cost: 30, desc: 'Armold spawns 50% more often and gives ×3 Frenzy.' },
  { id: 'tech_ship_scrapper', name: 'Star-Atlas Salvager', cost: 80, desc: 'Chests and bosses drop Star-Atlas Ship Scraps.' },
  { id: 'tech_blood_frenzy', name: 'Blood Moon Cultist', cost: 200, desc: 'Blood Moon yields ×5 drops and guaranteed rare loot.' },
];

export function getBuildingTechMult(clicker, buildingId) {
  const tech = clicker?.tech || {};
  const node = CLICKER_RESEARCH_TREE.find(t => t.buildingId === buildingId);
  return (node && tech[node.id]) ? node.mult : 1.0;
}

export const STAR_ATLAS_SHIP_COMPONENTS = [
  { id: 'hull', name: 'Titanium-Alloy Hull', req: 15, icon: 'shield', desc: '+10% Gold from all sources.' },
  { id: 'engine', name: 'Hyper-Drive Thruster', req: 40, icon: 'meteor', desc: '+15% Flux generation.' },
  { id: 'core', name: 'Quantum Water Core', req: 80, icon: 'orb', desc: '×2 Clicker Passive Drops.' },
  { id: 'bridge', name: 'Star-Atlas Holo-Deck', req: 160, icon: 'ui_eye', desc: '+5 Max Gems & +1 SP.' },
];

export const CLICKER_MANAGERS = [
  {
    id: 'arnold',
    buildingId: 'condenser',
    name: 'Armold Svartnmeger',
    title: 'Swedish-Austrian Terminator & Belgian Blue Enforcer',
    cost: 500,
    icon: 'pVamp',
    bio: '100% Swedish parody. Pumped to maximum vascularity on Belgian Blue bovine anabolics. Gallops into battle on his real-life dachshund-sized miniature pony with twin rotary cannons.',
    speedMult: 2.0,
  },
  {
    id: 'chemtrail_baroness',
    buildingId: 'net',
    name: 'The Contrail Baroness',
    title: 'High-Altitude Cloud Weaver',
    cost: 2500,
    icon: 'shield',
    bio: 'Owns 14 private jets spraying organic silver iodide mist over municipal reservoirs.',
    speedMult: 1.0,
  },
  {
    id: 'royal_reptilian',
    buildingId: 'fluoride',
    name: 'Queen Liz-Reptilia',
    title: 'Sovereign of the Scaly Commonwealth',
    cost: 12000,
    icon: 'trident',
    bio: 'Drinks classified Earl Grey with vertical reptilian slit pupils. Hides a 4-meter tail beneath royal ermine robes.',
    speedMult: 1.0,
  },
  {
    id: 'skull_president',
    buildingId: 'trap',
    name: 'Grandmaster Skully',
    title: 'Skull & Bones Grandmaster',
    cost: 60000,
    icon: 'orb',
    bio: 'Executes esoteric handshakes with lightning antennas. Speaks fluent thunderstorm Morse code.',
    speedMult: 1.0,
  },
  {
    id: 'president_dumb',
    buildingId: 'aquifer',
    name: 'President Dumb',
    title: 'CEO of DUMB TOWER & The Golden Escalator',
    cost: 350000,
    icon: 'beam',
    bio: 'Sporting the longest red tie in the hemisphere and a gravity-defying golden combover. Drills luxury gold boreholes right through bunker level 5.',
    speedMult: 1.0,
  },
  {
    id: 'tech_transhumanist',
    buildingId: 'coil',
    name: 'Elongated Muskrat',
    title: 'X-Oligarch & Cyber-Plumber',
    cost: 2000000,
    icon: 'bolt',
    bio: 'Replaced his nervous system with fiber-optic water pipes to optimize quarterly liquidity on Mars.',
    speedMult: 1.0,
  },
  {
    id: 'hollywood_cabal',
    buildingId: 'engine',
    name: 'Lady GaGaggle',
    title: 'Red-Carpet High Priestess',
    cost: 15000000,
    icon: 'meteor',
    bio: 'Chants pyramid alignment incantations between award show acceptance speeches.',
    speedMult: 1.0,
  },
  {
    id: 'lizard_senator',
    buildingId: 'bureau',
    name: 'Senator Cold-Blood',
    title: 'Sub-Committee on Humidity Control',
    cost: 100000000,
    icon: 'ui_eye',
    bio: 'Sheds full skin during filibusters. Successfully lobbied for mandatory heat lamps in congress.',
    speedMult: 1.0,
  },
  {
    id: 'annunaki_envoy',
    buildingId: 'annunaki',
    name: 'Annunaki Gold-Hoarder',
    title: 'Sumerian Orbital Well Custodian',
    cost: 800000000,
    icon: 'trident',
    bio: 'Golden 12-foot space monarch demanding 500 million litres of spring water tribute per orbit.',
    speedMult: 1.0,
  },
  {
    id: 'monolith_overseer',
    buildingId: 'monolith',
    name: 'The Cosmic Void Squid',
    title: 'Voice of the Void Pipeline',
    cost: 6000000000,
    icon: 'ui_void',
    bio: 'An omniscient crystalline entity that drinks entire multiverses through a bendy straw.',
    speedMult: 1.0,
  },
];

export function isBuildingAutomated(clicker, buildingId) {
  const managers = clicker?.managers || {};
  const mgr = CLICKER_MANAGERS.find(m => m.buildingId === buildingId);
  return mgr ? !!managers[mgr.id] : false;
}

// Click power scales off a bought upgrade AND a slice of passive production,
// so tapping stays worth doing late instead of becoming a rounding error.
export const CLICK_UPGRADE = { base: 40, growth: 1.28, per: 1 };

export function buildingCost(def, owned) {

  return Math.ceil(def.base * Math.pow(def.growth, owned));
}

export function clickUpgradeCost(lvl) {
  return Math.ceil(CLICK_UPGRADE.base * Math.pow(CLICK_UPGRADE.growth, lvl));
}

// ---- Springs (prestige) ----------------------------------------------------
// The loop a clicker actually runs on. Without a reset layer the whole mode is
// one long curve that flattens and then stops meaning anything: buildings get
// unaffordable, the rate stops moving, and there is no reason to open it
// again. Springs are the answer — you give up the run and keep a permanent
// multiplier, so the next pass is faster and going further is worth doing.
//
// Square-root gain is the genre standard for a good reason: linear gain makes
// resetting strictly better than continuing (so you reset constantly), while
// exponential gain makes the first reset the only one that matters. Sqrt
// means each reset is worth roughly the same effort as the last.
export const SPRING_THRESHOLD = 1e6;   // lifetime drops before the first Spring
export const SPRING_BONUS = 0.05;      // +5% to ALL production per Spring

export function springsFor(total) {
  if (!total || total < SPRING_THRESHOLD) return 0;
  return Math.floor(Math.sqrt(total / SPRING_THRESHOLD));
}

// How many Springs a reset would pay RIGHT NOW — always the amount the total
// has earned minus what has already been banked, so it can never go negative
// and can never pay for the same production twice.
export function prestigeGain(clicker) {
  return Math.max(0, springsFor(clicker?.total || 0) - (clicker?.springs || 0));
}

export function prestigeMult(clicker) {
  return 1 + (clicker?.springs || 0) * SPRING_BONUS;
}

// Returns a NEW clicker state; does not mutate. Lifetime `total`, claimed
// milestones and banked Springs survive — resetting must never cost you a
// milestone you already earned, or the reset becomes a punishment.
export function doPrestige(clicker) {
  const gain = prestigeGain(clicker);
  if (gain <= 0) return clicker;
  return {
    ...clicker,
    drops: 0,
    buildings: {},
    clickLvl: 0,
    springs: (clicker.springs || 0) + gain,
    prestiges: (clicker.prestiges || 0) + 1,
    // `total` is deliberately kept: it is lifetime production and the
    // milestone ladder is paid against it. Zeroing it here would silently
    // revoke every milestone the player has already claimed.
  };
}

export function dropsPerSec(clicker) {
  const owned = clicker?.buildings || {};
  let rate = 0;
  for (const def of CLICKER_BUILDINGS) {
    const techMult = getBuildingTechMult(clicker, def.id);
    rate += (owned[def.id] || 0) * def.rate * techMult;
  }
  return rate * prestigeMult(clicker);
}

export function dropsPerClick(clicker) {
  const lvl = clicker?.clickLvl || 0;
  // 1% of your per-second production folded into each tap.
  return 1 + lvl * CLICK_UPGRADE.per + Math.floor(dropsPerSec(clicker) * 0.01);
}

// ---- Milestones ------------------------------------------------------------
// Paid out against LIFETIME drops produced, claimed once each. This is the
// only bridge from the clicker into the rest of the economy, and it is
// deliberately a slow, chunky ladder rather than a steady trickle.
export const CLICKER_MILESTONES = [
  { at: 100,           reward: { gold: 250 },                       label: '250 gold' },
  { at: 1000,          reward: { gems: 1 },                         label: '1 gem' },
  { at: 10000,         reward: { gold: 900, gearParts: 15 },        label: '900 gold + 15 parts' },
  { at: 60000,         reward: { gems: 3, slotCoins: 2 },           label: '3 gems + 2 coins' },
  { at: 300000,        reward: { sp: 2, gearParts: 40 },            label: '2 SP + 40 parts' },
  { at: 1500000,       reward: { gachaTokens: 1, tcgCard: 1 },      label: '1 gacha token + a TCG card' },
  { at: 8000000,       reward: { rune: 1, gems: 6 },                label: 'a rune + 6 gems' },
  { at: 40000000,      reward: { sp: 6, gachaTokens: 2, tcgCard: 2 }, label: '6 SP + 2 tokens + 2 TCG cards' },
  { at: 200000000,     reward: { flux: 2, rune: 1, tcgCard: 3 },    label: '2 Flux + a rune + 3 TCG cards' },
  { at: 1000000000,    reward: { sp: 12, gems: 20, gachaTokens: 5 },label: '12 SP + 20 gems + 5 tokens' },
  { at: 5000000000,    reward: { flux: 5, rune: 2, tcgCard: 5 },    label: '5 Flux + 2 runes + 5 cards' },
  { at: 25000000000,   reward: { sp: 25, gems: 50, slotCoins: 10 }, label: '25 SP + 50 gems + 10 coins' },
  { at: 100000000000,  reward: { flux: 10, rune: 5, gachaTokens: 10 }, label: '10 Flux + 5 runes + 10 tokens' },
];

export function pendingMilestones(clicker) {
  const total = clicker?.total || 0;
  const claimed = clicker?.claimed || {};
  return CLICKER_MILESTONES.filter(m => total >= m.at && !claimed[m.at]);
}

export function nextMilestone(clicker) {
  const total = clicker?.total || 0;
  return CLICKER_MILESTONES.find(m => total < m.at) || null;
}

// Compact display for numbers that run to the hundreds of millions.
export function fmtDrops(n) {
  if (n < 1000) return Math.floor(n).toString();
  if (n < 1e6) return (n / 1e3).toFixed(n < 1e4 ? 2 : 1) + 'K';
  if (n < 1e9) return (n / 1e6).toFixed(n < 1e7 ? 2 : 1) + 'M';
  return (n / 1e9).toFixed(2) + 'B';
}

// ---- Conspiracy & Deep Clicker Features --------------------------------------

export const CONSPIRACY_CHAPTERS = [
  { id: 'ch1', name: 'The Drip',            threshold: 0,           theme: 'municipal',  headline: 'CHAPTER I' },
  { id: 'ch2', name: 'Fluoride Files',      threshold: 10000,       theme: 'fluoride',   headline: 'CHAPTER II' },
  { id: 'ch3', name: 'HAARP Directive',      threshold: 300000,      theme: 'haarp',      headline: 'CHAPTER III' },
  { id: 'ch4', name: 'Deep State Protocol',  threshold: 5000000,     theme: 'deepstate',  headline: 'CHAPTER IV' },
  { id: 'ch5', name: 'The Pyramid Scheme',   threshold: 50000000,    theme: 'pyramid',    headline: 'CHAPTER V' },
  { id: 'ch6', name: 'Reptilian Congress',   threshold: 500000000,   theme: 'reptilian',  headline: 'CHAPTER VI' },
  { id: 'ch7', name: 'Cosmic Convergence',   threshold: 10000000000, theme: 'cosmic',     headline: 'CHAPTER VII' },
];

export function currentChapter(clicker) {
  const total = clicker?.total || 0;
  for (let i = CONSPIRACY_CHAPTERS.length - 1; i >= 0; i--) {
    if (total >= CONSPIRACY_CHAPTERS[i].threshold) {
      return CONSPIRACY_CHAPTERS[i];
    }
  }
  return CONSPIRACY_CHAPTERS[0];
}

export const CHAPTER_HEADLINES = [
  [
    "THEY DON'T WANT YOU TO KNOW: 100% PURE H2O GRANTS SUPERPOWERS.",
    "LOCAL PLUMBER DISCOVERS TAP WATER IS 97% GOVERNMENT LIES.",
    "BREAKING: YOUR KITCHEN FAUCET HAS BEEN FILING REPORTS.",
    "WATER PRESSURE DROPS NATIONWIDE AS THE MAYOR TAKES A BATH.",
    "STUDY SHOWS ICE CUBES ARE JUST HARDENED SURVEILLANCE DEVICES.",
    "SHOWER THOUGHTS RECORDED AND UPLOADED TO THE CLOUD — LITERALLY.",
    "DON'T DRINK THE WATER: IT ENCOURAGES COMPLIANCE AND DAMPNESS."
  ],
  [
    "FLUORIDE CONCENTRATION DROPPED 0.02% — REPTILIAN SENATE CONVENES IN PANIC.",
    "DECLASSIFIED: FLUORIDE WAS NEVER ABOUT TEETH. IT WAS ABOUT OBEDIENCE.",
    "DENTISTS STRIKE GLOBALLY: 'WE CAN'T HIDE THE TRUTH ANYMORE.'",
    "THE TOOTH FAIRY IS A FED. WAKE UP.",
    "CAVITIES ARE JUST YOUR BONES ATTEMPTING TO BREATHE.",
    "NEW MOUTHWASH INTRODUCED: NOW WITH 50% MORE AMNESIA.",
    "YOUR GUMS ARE BLEEDING BECAUSE THEY KNOW TOO MUCH."
  ],
  [
    "HAARP ARRAY RECALIBRATED: ALL RAIN NOW FALLS UPWARD IN SECTOR 7.",
    "WEATHER REPORT: SUNNY EVERYWHERE EXCEPT THE PARTS THEY DON'T WANT YOU TO SEE.",
    "METEOROLOGISTS ADMIT 'CLOUDS' ARE JUST GIANT COTTON BALLS HUNG BY WIRES.",
    "LIGHTNING STRIKES LOCAL MAN WHO ASKED TOO MANY QUESTIONS.",
    "HURRICANE REDIRECTED TO AVOID ELITE GOLF COURSE.",
    "BAROMETRIC PRESSURE USED TO CONTROL LOCAL ELECTION RESULTS.",
    "THE SKY IS FALLING, BUT ONLY ON TUESDAYS BETWEEN 2 AND 4 PM."
  ],
  [
    "LEAKED MEMO: BUNKER LEVEL 4 IS JUST A VERY LARGE AQUARIUM.",
    "ANONYMOUS SOURCE: THE DEEP STATE KEEPS ITS BEST WATER IN LEVEL 5.",
    "PENTAGON ADMITS THEY DON'T KNOW WHAT A PENTAGON IS ANYMORE.",
    "SHADOW GOVERNMENT CAUGHT EMBEZZLING TAP WATER FUNDS.",
    "PRESIDENT'S TIE COLOR SIGNALS IMMINENT WATER RATIONING.",
    "MEN IN BLACK REPLACED WITH MEN IN KHAKIS TO SAVE BUDGET.",
    "SECRET SERVICE CANNOT CONFIRM IF THE OCEAN ACTUALLY EXISTS."
  ],
  [
    "SCIENTISTS CONFIRM: PYRAMIDS ARE JUST VERY POINTY WELLS.",
    "SACRED GEOMETRY ALERT: YOUR THIRD EYE IS ACTUALLY A FAUCET.",
    "SPHINX NOSE REMOVED TO PREVENT IT FROM SMELLING THE LIES.",
    "MUMMIES WERE JUST EARLY ATTEMPTS AT DEHYDRATION PRESERVATION.",
    "ALIEN ARCHITECTS SUE PHARAOHS FOR UNPAID ROYALTIES.",
    "HIEROGLYPHS TRANSLATED: 'DO NOT DRINK UNBOILED.'",
    "THE ILLUMINATI NOW ACCEPTING APPLICATIONS FOR PLUMBERS."
  ],
  [
    "SENATE HEARING POSTPONED: ALL SENATORS CURRENTLY SHEDDING.",
    "CLASSIFIED: REPTILIAN BUREAU ANNUAL BUDGET IS 100% CONDENSED TEARS.",
    "GLOBAL WARMING ACCELERATED TO ACCOMMODATE COLD-BLOODED LIZARDS.",
    "CRICKETS NOW OUTSELL STEAK IN PARLIAMENT CAFETERIA.",
    "LIZARD PEOPLE DENY INVOLVEMENT IN RECENT HEAT WAVE.",
    "NEW LAW BANS HEAT LAMPS UNLESS YOU HAVE CLEARANCE.",
    "TAIL REGROWTH SUBSIDIZED BY TAXPAYER MONEY."
  ],
  [
    "ANNUNAKI MOTHERSHIP STATUS: SPRINKLER SYSTEM ACTIVE.",
    "THE MONOLITH SPEAKS: IT SAYS 'GLUG.'",
    "INTERGALACTIC WATER WAR AVERTED BY A WELL-TIMED TEA BREAK.",
    "MARTIAN CANALS CONFIRMED TO BE JUST REALLY BIG SLIP-AND-SLIDES.",
    "BLACK HOLE REVEALED TO BE A MASSIVE PLUG HOLE.",
    "STARS ARE JUST LIGHT REFLECTING OFF THE GREAT COSMIC PUDDLE.",
    "THE UNIVERSE IS EXPANDING BECAUSE SOMEONE LEFT THE HOSE ON."
  ]
];

export function headlinesForChapter(clicker) {
  const current = currentChapter(clicker);
  const chapterIndex = CONSPIRACY_CHAPTERS.findIndex(c => c.id === current.id);
  let headlines = [];
  for (let i = 0; i <= chapterIndex; i++) {
    headlines = headlines.concat(CHAPTER_HEADLINES[i]);
  }
  return headlines;
}

export const CONSPIRACY_DOSSIERS = [
  { id: 'dossier_1', title: 'MEMO 7: PACIFICATION', text: "The fluoride wasn't meant to pacify humans. It was designed to pacify the WATER. — Declassified, A.I.D.A. Archives", condition: (c) => (c?.total || 0) >= 5000 },
  { id: 'dossier_2', title: 'FILE 12: THE DRIP', text: "A dripping faucet isn't a leak. It's Morse code from the pipes. — Agent 42", condition: (c) => (c?.buildings?.condenser || 0) >= 5 },
  { id: 'dossier_3', title: 'REPORT 88: CLOUDS', text: "Chemtrails are just the sky's way of sweating out the government's lies. — Anonymous", condition: (c) => (c?.buildings?.net || 0) >= 1 },
  { id: 'dossier_4', title: 'LOG 01: BEGINNINGS', text: "It starts with a drop. It ends with an ocean of truth. — The First Plumber", condition: (c) => (c?.total || 0) >= 100 },
  { id: 'dossier_5', title: 'MEMO 14: EFFICIENCY', text: "Tin foil doesn't block the signals, it amplifies them directly into the water. — Unredacted", condition: (c) => (c?.buildings?.condenser || 0) >= 20 },
  { id: 'dossier_6', title: 'FILE 22: BUREAU PURPOSE', text: "The Reptilian Bureau's official purpose: 'condensing tears from redacted government files.' Their actual purpose: [THIS LINE REMOVED BY ORDER OF THE EYE]", condition: (c) => (c?.buildings?.bureau || 0) >= 1 },
  { id: 'dossier_7', title: 'DOSSIER 14: THE DYNAMO', text: "The Pyramid Dynamo doesn't generate power. It generates OBEDIENCE. The drops just happen to be a side effect. — Agent [REDACTED]", condition: (c) => (c?.buildings?.engine || 0) >= 1 },
  { id: 'dossier_8', title: 'REPORT 19: THE WELL', text: "If you stare long enough into the deep state borehole, it stares back. And asks for a towel. — Subterranean Scout", condition: (c) => (c?.buildings?.aquifer || 0) >= 1 },
  { id: 'dossier_9', title: 'LOG 404: MISSING WATER', text: "We lose 40% of our water to 'evaporation'. That's what they call the invisible tax collectors. — Defector 9", condition: (c) => (c?.total || 0) >= 100000 },
  { id: 'dossier_10', title: 'MEMO 33: TAPPING', text: "Every time you tap, a politician forgets a promise. Keep tapping. — The Resistance", condition: (c) => (c?.clickLvl || 0) >= 10 },
  { id: 'dossier_11', title: 'FILE 45: SPRING WATER', text: "Spring water isn't from a spring. It's the reset button of the simulation. — Chronicler", condition: (c) => (c?.prestiges || 0) >= 1 },
  { id: 'dossier_12', title: 'DOSSIER 99: PRESTIGE', text: "To prestige is to forget everything except the thirst. — Wisdom of the Ancients", condition: (c) => (c?.prestiges || 0) >= 3 },
  { id: 'dossier_13', title: 'REPORT 77: HAARP', text: "HAARP doesn't control the weather. It controls your perception of umbrellas. — Meteorologist X", condition: (c) => (c?.buildings?.trap || 0) >= 5 },
  { id: 'dossier_14', title: 'LOG 88: THE COIL', text: "The subliminal coil hums at 432Hz. This is the exact frequency of a disappointed sigh. — Acoustical Engineer", condition: (c) => (c?.buildings?.coil || 0) >= 1 },
  { id: 'dossier_15', title: 'MEMO 55: FLUORIDE', text: "Fluoride scrubbers don't remove fluoride. They add micro-crystals of pure anxiety. — Dental Whistleblower", condition: (c) => (c?.buildings?.fluoride || 0) >= 10 },
  { id: 'dossier_16', title: 'FILE 66: THE MOTHERSHIP', text: "The Annunaki don't want our gold. They want our sparkling water. — Abductee 004", condition: (c) => (c?.buildings?.annunaki || 0) >= 1 },
  { id: 'dossier_17', title: 'DOSSIER 77: THE MONOLITH', text: "The Cosmic Monolith is just a giant vending machine that ran out of change. — Astronaut Y", condition: (c) => (c?.buildings?.monolith || 0) >= 1 },
  { id: 'dossier_18', title: 'REPORT 23: GEAR PARTS', text: "Those aren't gear parts. They're fragments of the broken firmament. — Mechanic Bob", condition: (c) => (c?.total || 0) >= 10000 },
  { id: 'dossier_19', title: 'LOG 11: GEMS', text: "Gems are just fossilized drops of water that knew too much. — Geologist Z", condition: (c) => (c?.total || 0) >= 60000 },
  { id: 'dossier_20', title: 'MEMO 99: FLUX', text: "Flux is the government's way of measuring how much reality is left to bend. — Physicist Q", condition: (c) => (c?.total || 0) >= 200000000 },
  { id: 'dossier_21', title: 'FILE 88: MASS PRODUCTION', text: "When you have 100 condensers, you aren't catching water. You're catching the air's soul. — Philosopher King", condition: (c) => (c?.buildings?.condenser || 0) >= 100 },
  { id: 'dossier_22', title: 'DOSSIER 42: SIPHON SURGE', text: "Chemtrail siphons at maximum capacity just produce a very polite drizzle. — Aviation Authority", condition: (c) => (c?.buildings?.net || 0) >= 50 },
  { id: 'dossier_23', title: 'REPORT 05: THE VOID', text: "A billion drops is a drop in the cosmic bucket. Keep clicking. — The Overseer", condition: (c) => (c?.total || 0) >= 1000000000 },
  { id: 'dossier_24', title: 'LOG 73: UPGRADES', text: "Clicking harder doesn't produce more water. It just annoys the simulation administrators into paying you off. — IT Support", condition: (c) => (c?.clickLvl || 0) >= 25 },
  { id: 'dossier_25', title: 'MEMO 11: REPTILIANS', text: "The Reptilians don't hide among us. They just have really good PR reps. — Media Insider", condition: (c) => (c?.buildings?.bureau || 0) >= 5 },
  { id: 'dossier_26', title: 'FILE 33: ALIEN TECH', text: "Annunaki wellsprings are heavily regulated by the intergalactic zoning commission. — Galactic Planner", condition: (c) => (c?.buildings?.annunaki || 0) >= 5 },
  { id: 'dossier_27', title: 'DOSSIER 19: THE CORE', text: "Staring directly at the Monolith Core causes mild existential dread and a craving for salt. — Health Warning", condition: (c) => (c?.buildings?.monolith || 0) >= 2 },
  { id: 'dossier_28', title: 'REPORT 91: ENDLESS CYCLE', text: "Ten prestiges in, and you realize the water was drinking you all along. — Zen Master", condition: (c) => (c?.prestiges || 0) >= 10 },
  { id: 'dossier_29', title: 'LOG 55: HYDRATION', text: "Hydration is a myth sold by Big Water. Thirst is the only truth. — The Dehydrated Prophet", condition: (c) => (c?.total || 0) >= 5000000000 },
  { id: 'dossier_30', title: 'MEMO 00: AWAKENING', text: "You've collected more water than the oceans hold. The simulation is cracking. Wake up. — Admin", condition: (c) => (c?.total || 0) >= 100000000000 }
];

export function unlockedDossiers(clicker) {
  return CONSPIRACY_DOSSIERS.filter(d => d.condition(clicker));
}

export const CLICKER_EVENTS = [
  { id: 'haarp_surge',    name: 'HAARP SURGE',        mult: 3,  duration: 12, icon: 'cloud',   desc: 'All production ×3 for 12 seconds!', minChapter: 3 },
  { id: 'deep_leak',      name: 'DEEP STATE LEAK',    tapMult: 10, taps: 5, icon: 'beam', desc: 'Next 5 taps worth ×10 each!', minChapter: 4 },
  { id: 'pyramid_align',  name: 'PYRAMID ALIGNMENT',  mult: 2,  duration: 15, icon: 'meteor',  desc: 'All buildings produce double!', minChapter: 5 },
  { id: 'fluoride_purge', name: 'FLUORIDE PURGE',      burstSec: 30, icon: 'pVamp', desc: 'Instant burst of 30 seconds of production!', minChapter: 2 },
  { id: 'eye_opens',      name: 'THE EYE OPENS',      mult: 5,  duration: 8, icon: 'ui_eye',   desc: 'The Eye sees all. ×5 for 8 seconds!', minChapter: 6 },
  { id: 'cosmic_tide',    name: 'COSMIC TIDE',        mult: 10, duration: 5, icon: 'trident',  desc: 'The cosmos aligns. ×10 for 5 seconds!', minChapter: 7 },
];

export function rollEvent(clicker) {
  const current = currentChapter(clicker);
  const chapterIndex = CONSPIRACY_CHAPTERS.findIndex(c => c.id === current.id);
  const chapterNumber = chapterIndex + 1;
  const validEvents = CLICKER_EVENTS.filter(e => e.minChapter <= chapterNumber);
  if (validEvents.length === 0) return null;
  return validEvents[Math.floor(Math.random() * validEvents.length)];
}

/**
 * Returns player's Star-Atlas spaceship details from save for clicker map travel & prestige.
 * Reads purely from save without touching or mutating Star Atlas structures.
 */
export function getEquippedShipDetails(save) {
  const colorId = save?.profile?.shipColor || 'teal';
  const colorMap = {
    teal: '#5fd6ff',
    gold: '#ffd166',
    crimson: '#ff3366',
    emerald: '#4dffd4',
    violet: '#b362ff',
    white: '#ffffff',
    obsidian: '#334455',
  };
  const shipColor = colorMap[colorId] || (colorId.startsWith('#') ? colorId : '#5fd6ff');
  const shipId = save?.starRaid?.shipId || 'skiff';
  const shipName = shipId === 'gunship' ? 'Bulwark Gunship' : shipId === 'cruiser' ? 'Vanguard Cruiser' : 'Astro Skiff';
  return { shipId, shipColor, shipName };
}

